import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { AuthLang } from './AuthModal';

export type RecognitionSyncReport = {
  at: number;
  geminiOk: boolean;
  geminiError?: string;
  geminiFixtureCount: number;
  depthNormalOk: boolean;
  depthError?: string;
  depthImageDataUrl?: string;
  normalImageDataUrl?: string;
  spatialMappingOk?: boolean;
  spatialMappingError?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  report: RecognitionSyncReport | null;
  language: AuthLang;
};

const RecognitionSyncReportModal: React.FC<Props> = ({ isOpen, onClose, report, language }) => {
  const zh = language === 'zh';

  const Row = ({ ok, label, detail }: { ok: boolean; label: string; detail?: string }) => (
    <div className="flex flex-col gap-1 py-2 border-b border-white/10 last:border-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-white/85">{label}</span>
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded ${
            ok ? 'bg-emerald-500/25 text-emerald-300' : 'bg-red-500/25 text-red-300'
          }`}
        >
          {ok ? (zh ? '成功' : 'OK') : zh ? '失败' : 'Fail'}
        </span>
      </div>
      {detail ? <p className="text-[11px] text-white/45 break-all leading-relaxed">{detail}</p> : null}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && report && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="w-full max-w-md rounded-xl border border-white/15 bg-zinc-900/95 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[#D7CDA1]">
                {zh ? '识别 · 并行请求结果' : 'Recognize · parallel requests'}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-white/50 hover:text-white text-lg leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
              <Row
                ok={report.geminiOk}
                label={zh ? '① Gemini 灯具识别' : '① Gemini fixture analysis'}
                detail={
                  report.geminiOk
                    ? zh
                      ? `灯具数量：${report.geminiFixtureCount}`
                      : `Fixtures: ${report.geminiFixtureCount}`
                    : report.geminiError
                }
              />

              <Row
                ok={report.depthNormalOk}
                label={zh ? '② RunningHub 深度 + 法线' : '② RunningHub depth + normal'}
                detail={report.depthNormalOk ? undefined : report.depthError}
              />

              {report.depthNormalOk ? (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <p className="text-[10px] text-white/50 mb-1">{zh ? '深度图预览' : 'Depth'}</p>
                    {report.depthImageDataUrl ? (
                      <img
                        src={report.depthImageDataUrl}
                        alt="depth"
                        className="w-full rounded border border-white/10 object-cover aspect-video bg-black"
                      />
                    ) : (
                      <div className="w-full aspect-video rounded bg-black/40 border border-white/10" />
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-white/50 mb-1">{zh ? '法线图预览' : 'Normal'}</p>
                    {report.normalImageDataUrl ? (
                      <img
                        src={report.normalImageDataUrl}
                        alt="normal"
                        className="w-full rounded border border-white/10 object-cover aspect-video bg-black"
                      />
                    ) : (
                      <div className="w-full aspect-video rounded bg-black/40 border border-white/10" />
                    )}
                  </div>
                </div>
              ) : null}

              {report.geminiOk && report.depthNormalOk ? (
                <div className="mt-3 pt-2 border-t border-white/10">
                  <Row
                    ok={report.spatialMappingOk !== false}
                    label={zh ? '③ 离屏空间映射 processSpatialMapping' : '③ Offscreen spatial mapping'}
                    detail={report.spatialMappingError}
                  />
                </div>
              ) : null}

              <p className="text-[10px] text-white/35 mt-3">
                {zh ? '时间：' : 'Time: '}
                {new Date(report.at).toLocaleString()}
              </p>
            </div>

            <div className="px-4 py-3 border-t border-white/10 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm bg-[#D7CDA1]/20 text-[#D7CDA1] hover:bg-[#D7CDA1]/30"
              >
                {zh ? '关闭' : 'Close'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecognitionSyncReportModal;
